import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VentasService } from 'src/app/services/ventas.service';
import { PagosService } from 'src/app/services/pagos.service';
import { ToastrService } from 'ngx-toastr';
import { GenericFormHandlerService } from 'src/app/shared/generic-crud.service';
import { controlHttp } from 'src/app/shared/models/form.models';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MetodoPago, METODO_PAGO_LABELS } from 'src/app/enums/metodo-pago.enum';

@Component({
  selector: 'app-ventas-dialog-mobile',
  templateUrl: './ventas-dialog-mobile.component.html',
  styleUrls: ['./ventas-dialog-mobile.component.scss']
})
export class VentasDialogMobileComponent extends GenericFormHandlerService implements OnInit {
  ventaDetalles: any;
  vendedorNombre: string | null = null;
  pagos: any[] = [];
  mostrarFormularioAbono = false;
  abonoForm: FormGroup;
  totalPagado = 0;
  saldoPendiente = 0;
  @Input() ventaId: number;
  MetodoPago = MetodoPago;
  metodosPago = Object.values(MetodoPago);
  metodoPagoLabels = METODO_PAGO_LABELS;

  constructor(
    protected ventaService: VentasService,
    protected toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef?: MatDialogRef<VentasDialogMobileComponent>,
    private pagosService: PagosService = null as any,
    private fb: FormBuilder = null as any
  ) {
    super(ventaService, toastr, dialogRef);
    this.ventaId = this.data?.venta?.id;
    this.configureBasedOnData();
    // Inicializar formulario de abono
    this.abonoForm = this.fb ? this.fb.group({
      monto: [0, [Validators.required, Validators.min(0.01)]],
      metodoPago: [MetodoPago.EFECTIVO, Validators.required]
    }) : new FormGroup({});
  }

  ngOnInit(): void {
    this.obtenerDetalles();
    this.initializeForm(false);
  }

  private configureBasedOnData(): void {
    this.currentOperation = controlHttp.READ;
    if (this.data.venta) {
      this.apiIdentifier = this.data.venta.id;
    }
  }

  getEstadoColor(estado: string): string {
    switch (estado) {
      case 'completada': return 'primary';
      case 'pendiente': return 'accent';
      case 'cancelada': return 'warn';
      default: return '';
    }
  }

  imprimirTicket(): void {
    this.toastr.success('Ticket enviado a impresión', 'Éxito');
    this.dialogRef?.close();
  }

  exportarPdf(): void {
    if (!this.ventaDetalles) {
      this.toastr.warning('No hay información para exportar');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const margin = 36;
    const lineHeight = 18;
    let cursorY = margin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Ticket - Detalles de Venta', margin, cursorY);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    cursorY += lineHeight * 1.5;
    doc.text(`No. Ticket: ${this.ventaDetalles.id}`, margin, cursorY);
    doc.text(`Fecha: ${new Date(this.ventaDetalles.createdAt).toLocaleString()}`, margin + 220, cursorY);

    cursorY += lineHeight;
    const vendedorNombre = typeof this.ventaDetalles.usuario === 'string'
      ? this.ventaDetalles.usuario
      : this.ventaDetalles.usuario?.name;
    doc.text(`Vendedor: ${vendedorNombre || 'N/D'}`, margin, cursorY);
    doc.text(`Estado: ${this.ventaDetalles.estado}`, margin + 220, cursorY);

    cursorY += lineHeight * 1.5;
    doc.setFont('helvetica', 'bold');
    doc.text('Productos', margin, cursorY);

    const body = (this.ventaDetalles.detalles || []).map((detalle: any, index: number) => {
      const producto = detalle.producto || {};
      return [
        index + 1,
        producto.nombre || 'N/D',
        producto.proveedorNombre || 'Sin proveedor',
        detalle.cantidad,
        Number(detalle.precioUnitario || 0).toFixed(2),
        Number(detalle.subtotal || 0).toFixed(2)
      ];
    });

    autoTable(doc, {
      startY: cursorY + lineHeight / 2,
      head: [['#', 'Producto', 'Proveedor', 'Cant.', 'P. Unitario', 'Subtotal']],
      body,
      theme: 'grid',
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 251] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 32 },
        1: { cellWidth: 150 },
        2: { cellWidth: 120 },
        3: { halign: 'center', cellWidth: 45 },
        4: { halign: 'right', cellWidth: 70 },
        5: { halign: 'right', cellWidth: 70 }
      },
      styles: { font: 'helvetica', fontSize: 10 }
    });

    const finalY = (doc as any).lastAutoTable.finalY || cursorY + lineHeight;
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen', margin, finalY + lineHeight);

    doc.setFont('helvetica', 'normal');
    const resumenY = finalY + lineHeight * 1.5;
    doc.text(`Subtotal: ${Number(this.ventaDetalles.total - this.ventaDetalles.impuesto || 0).toFixed(2)}`, margin, resumenY);
    doc.text(`Impuesto: ${Number(this.ventaDetalles.impuesto || 0).toFixed(2)}`, margin + 200, resumenY);
    doc.text(`Total: ${Number(this.ventaDetalles.total || 0).toFixed(2)}`, margin + 350, resumenY);

    const pagoY = resumenY + lineHeight;
    doc.text(`Recibido: ${Number(this.ventaDetalles.dineroRecibido || 0).toFixed(2)}`, margin, pagoY);
    doc.text(`Cambio: ${Number(this.ventaDetalles.cambio || 0).toFixed(2)}`, margin + 200, pagoY);

    const footerY = doc.internal.pageSize.getHeight() - margin;
    doc.setFontSize(10);
    doc.text('¡Gracias por su compra!', margin, footerY - lineHeight);
    doc.text('Para devoluciones, presente este ticket dentro de los 15 días', margin, footerY);

    doc.save(`ticket-venta-${this.ventaDetalles.id}.pdf`);
    this.toastr.success('PDF generado correctamente', 'Exportación');
  }

  onCancel(): void {
    this.dialogRef?.close();
  }

  obtenerDetalles(): void {
    this.isLoading = true;
    this.ventaService.get(`${this.data?.venta?.id}/detalles`).subscribe({
      next: (venta) => {
        this.ventaDetalles = venta;
        this.vendedorNombre = typeof venta.usuario === 'string'
          ? venta.usuario
          : venta.usuario?.name ?? null;
        this.cargarPagos();
        this.isLoading = false;
      },
      error: (error) => {
        const errorMessage = this.extractErrorMessage(error);
        this.toastr.error(errorMessage || 'Error al cargar los datos');
        this.isLoading = false;
      }
    });
  }

  cargarPagos(): void {
    if (!this.ventaDetalles?.id || !this.pagosService) return;

    this.pagosService.obtenerPagosPorVenta(this.ventaDetalles.id).subscribe({
      next: (pagos) => {
        // Ordenar pagos por fecha (más reciente primero)
        this.pagos = (pagos || []).sort((a, b) => {
          const fechaA = new Date(a.createdAt).getTime();
          const fechaB = new Date(b.createdAt).getTime();
          return fechaB - fechaA; // Orden descendente (más reciente primero)
        });
        this.calcularTotales();
      },
      error: () => {
        this.pagos = [];
        this.calcularTotales();
      }
    });
  }

  calcularTotales(): void {
    // Convertir montos de string a número para sumar correctamente
    this.totalPagado = this.pagos.reduce((sum, pago) => {
      const monto = typeof pago.monto === 'string' ? parseFloat(pago.monto) : (pago.monto || 0);
      return sum + (isNaN(monto) ? 0 : monto);
    }, 0);
    
    // Redondear a 2 decimales para evitar errores de precisión
    this.totalPagado = Math.round(this.totalPagado * 100) / 100;
    
    if (this.ventaDetalles) {
      // Convertir total de string a número si es necesario
      const total = typeof this.ventaDetalles.total === 'string' 
        ? parseFloat(this.ventaDetalles.total) 
        : (this.ventaDetalles.total || 0);
      this.saldoPendiente = Math.round((total - this.totalPagado) * 100) / 100;
    }
  }

  toggleFormularioAbono(): void {
    this.mostrarFormularioAbono = !this.mostrarFormularioAbono;
    if (this.mostrarFormularioAbono && this.abonoForm) {
      this.abonoForm.patchValue({ monto: Math.max(0, this.saldoPendiente) });
    }
  }

  registrarAbono(): void {
    if (!this.pagosService || !this.abonoForm || this.abonoForm.invalid) {
      this.toastr.warning('Ingrese un monto válido');
      return;
    }

    const monto = this.abonoForm.get('monto')?.value;
    if (monto <= 0) {
      this.toastr.warning('El monto debe ser mayor a cero');
      return;
    }

    if (monto > this.saldoPendiente) {
      this.toastr.warning(`El monto no puede ser mayor al saldo pendiente (${this.saldoPendiente})`);
      return;
    }

    this.isLoading = true;
    const metodoPago = this.abonoForm?.get('metodoPago')?.value;
    var dtoAbono = {
      monto: monto,
      metodoPago: metodoPago
    }
    this.pagosService.post(dtoAbono, `venta/${this.ventaId}/abono`).subscribe({
      next: () => {
        this.toastr.success('Abono registrado exitosamente');
        if (this.abonoForm) {
          this.abonoForm.reset();
        }
        this.mostrarFormularioAbono = false;
        this.cargarPagos();
        // Recargar detalles de venta para actualizar el estado
        this.obtenerDetalles();
      },
      error: (error) => {
        const errorMessage = this.extractErrorMessage(error);
        this.toastr.error(errorMessage || 'Error al registrar el abono');
        this.isLoading = false;
      }
    });
  }

  puedeAgregarAbono(): boolean {
    // Permitir agregar abono si la venta está pendiente y hay saldo pendiente (con tolerancia para errores de redondeo)
    return this.ventaDetalles?.estado === 'pendiente' && this.saldoPendiente > 0.01;
  }

  mostrarSeccionAbonos(): boolean {
    // Mostrar si tiene abonos (ya sea pendiente o completada)
    return this.pagos.length > 0;
  }

  esVentaConAbonos(): boolean {
    // Verificar si la venta tiene múltiples abonos (no es pago único)
    return this.pagos.length > 0;
  }
}

interface DialogData {
  venta?: any;
  isEdit?: boolean;
}

